import React, { useState, useEffect, Suspense } from 'react';
import { API_DEFAULT } from "../config/xano";
import { useObjectStore } from "../zustand/objects";
import { useAccountStore } from "../zustand/account";
import { IconTrash } from '@tabler/icons-react';

import { Button, Text, Group, FileButton, ActionIcon, Tabs, Modal, SimpleGrid, Stack, Box } from '@mantine/core';
import {
  editorModalShell,
  editorModalStrongTitleStyles,
  editorModalPrimaryButtonStyles,
  editorModalPrimaryButtonCompactStyles,
  editorModalGalleryPanelStyle,
  editorModalThumbFrameStyle,
  editorModalFootnoteBoxStyle,
} from "../styleFile";

import StagedUploadedModel from './StagedUploadedModel'
import ImagesAPIMapped from "./ImagesAPIMapped";

import { nanoid } from 'nanoid'


export function UploadModal({ open, close }) {

  const {
    arrangeUndoRedo,
    currentObjectArtboard,
    addObject
  } = useObjectStore();

  const { userStatus } = useAccountStore();
  const viewportThemeOverride = useObjectStore((state) => state.viewportThemeOverride);
  const isDarkUi = viewportThemeOverride === "dark";
  const uploadTabsTheme = {
    listBorder: isDarkUi ? "#475569" : "#E5E7EB",
    listBg: isDarkUi ? "#2f2f2f" : "#FFFFFF",
    tabText: "#c1c2c5",
    tabTextActive: isDarkUi ? "#E2E8F0" : "#111827",
    tabUnderline: "#FE9A00",
  };

  const [myModels, setMyModels] = useState([])


  //  GET ALL UPLOADED MODELS
  const getUserModelsAPI = () => {
    return fetch(API_DEFAULT + '/model', {
      method: 'GET',
      headers: {
        "content-type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem('user')}`
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setMyModels(data)
      })
      .catch((error) => {
        console.error("Error:", error);

      });
  }

  useEffect(() => {
    getUserModelsAPI()
  }, [])

  useEffect(() => {
    setUploadAlert(myModels.length >= 5)
  }, [myModels.length])



  const uploadModelAPI = (data) => {

    return fetch(API_DEFAULT + '/model', {
      method: 'POST',
      headers: {
        "Authorization": `Bearer ${localStorage.getItem('user')}`
      },
      body: data

    })
      .then((response) => response.json())
      .then((data) => {
        getUserModelsAPI()
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }


  const handleDeleteAPI = (data) => {
    return fetch(API_DEFAULT + '/model/' + data, {
      method: 'DELETE',
      headers: {
        "Authorization": `Bearer ${localStorage.getItem('user')}`
      },
      body: data

    })
      .then((response) => response.json())
      .then(() => {
        getUserModelsAPI()
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }


  //  add it to scene 
  const handleSelect = (e) => {
    const newID = nanoid()
    // send update to zustand first (so we have state to grab for undo array)
    addObject({
      currentObjectArtboard,
      category: 'uploaded',
      object: e.name,
      undo: false,
      id: newID,
      position: null,
      scale: null,
      rotation: null,
      matrix: null,
      pose: {
        name: null,
        modelPath: e.model.url
      },
      imageTexture: null,
      image: null,
      curved: false,
      curvedAmount: 3,
      uploadedURL: e.model.url,
      bGColour: null,
      uploadId: e.id
    })
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "objectAdd",
      artboard: currentObjectArtboard,
      objectID: newID,
      undo: false
    })

  }

  const [sizeAlert, setSizeAlert] = useState(false)
  const [uploadAlert, setUploadAlert] = useState(false)


  return (
    <Modal
      opened={open}
      onClose={close}
      title="Library"
      withCloseButton
      size={560}
      radius={editorModalShell.radius}
      centered={editorModalShell.centered}
      overlayProps={editorModalShell.overlayProps}
      styles={editorModalStrongTitleStyles}
    >
      <Tabs defaultValue="images">
        <Tabs.List
          style={{
            height: 37,
            padding: 0,
            gap: 0,
            borderBottom: `1px solid ${uploadTabsTheme.listBorder}`,
            alignItems: "stretch",
            backgroundColor: uploadTabsTheme.listBg,
          }}
        >
          <Tabs.Tab
            value="images"
            px={16}
            py={0}
            style={{ borderRadius: 0 }}
            sx={{
              height: 36,
              paddingBottom: 2,
              borderRadius: "4px 4px 0 0",
              borderBottom: "2px solid transparent",
              marginBottom: -1,
              color: uploadTabsTheme.tabText,
              "&[data-active='true']": {
                borderBottomColor: uploadTabsTheme.tabUnderline,
                color: uploadTabsTheme.tabTextActive,
              },
            }}
          >
            <Text
              fw={400}
              size={14}
              style={{
                letterSpacing: "-0.15px",
                lineHeight: "14px",
              }}
            >
              Images
            </Text>
          </Tabs.Tab>
          <Tabs.Tab
            value="models"
            px={16}
            py={0}
            style={{ borderRadius: 0 }}
            sx={{
              height: 36,
              paddingBottom: 2,
              borderRadius: "4px 4px 0 0",
              borderBottom: "2px solid transparent",
              marginBottom: -1,
              color: uploadTabsTheme.tabText,
              "&[data-active='true']": {
                borderBottomColor: uploadTabsTheme.tabUnderline,
                color: uploadTabsTheme.tabTextActive,
              },
            }}
          >
            <Text
              fw={400}
              size={14}
              style={{
                letterSpacing: "-0.15px",
                lineHeight: "14px",
              }}
            >
              Models
            </Text>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="models">
          <Box style={editorModalGalleryPanelStyle}>
            {myModels.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py={32} px="md" style={{ lineHeight: 1.5 }}>
                No uploads yet. Add a .glb below to see it here.
              </Text>
            ) : (
              <SimpleGrid cols={3} spacing={12}>
                {myModels.map((model, i) => (
                  <Stack key={i} gap={10} align="center">
                    <Box
                      style={{
                        ...editorModalThumbFrameStyle,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Suspense fallback={null}>
                        <StagedUploadedModel url={model.model.url} />
                      </Suspense>
                    </Box>
                    <Text size="sm" fw={600} lineClamp={2} ta="center" style={{ minHeight: 40, lineHeight: 1.35, width: "100%" }}>
                      {model.name}
                    </Text>
                    <Group gap={8} justify="center" wrap="nowrap">
                      <Button onClick={() => handleSelect(model)} styles={editorModalPrimaryButtonCompactStyles}>
                        Add
                      </Button>
                      <ActionIcon
                        variant="light"
                        color="gray"
                        size="md"
                        radius="md"
                        aria-label="Delete model"
                        onClick={() => handleDeleteAPI(model.id)}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    </Group>
                  </Stack>
                ))}
              </SimpleGrid>
            )}
          </Box>
          <Stack gap="md" p={20}>
            <Text size="sm" fw={600} c="var(--mbx-modal-primary-text, #111827)">
              Upload your own model
            </Text>
            {userStatus === "loggedIn-none" && (
              <Text size="sm" c="dimmed" style={{ lineHeight: 1.5 }}>
                <a href="https://mbxr.io/account" target="_blank" rel="noreferrer" style={{ color: "var(--mantine-color-blue-6)", fontWeight: 500 }}>
                  Upgrade to premium
                </a>{" "}
                to upload 3D models.
              </Text>
            )}
            <Group gap="sm" wrap="wrap" align="center">
              <FileButton
                accept=".glb"
                onChange={(e) => {
                  const form = new FormData();
                  if (e.size > 1097152) {
                    setSizeAlert(true);
                  } else {
                    setSizeAlert(false);
                    form.append("model", e, e.name.substring(0, 15) + ".glb");
                    e && uploadModelAPI(form);
                  }
                }}
              >
                {(props) => (
                  <Button
                    {...props}
                    disabled={uploadAlert || userStatus === "loggedIn-none"}
                    radius={4}
                    styles={editorModalPrimaryButtonStyles}
                  >
                    Upload
                  </Button>
                )}
              </FileButton>
            </Group>
            <Box p="md" style={editorModalFootnoteBoxStyle}>
              <Stack gap={6}>
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.55 }}>
                  Only .glb files, max 1&nbsp;MB each. You can store up to five models in your library.
                </Text>
                {sizeAlert && (
                  <Text c="red" size="xs" fw={500}>
                    This file is over 1&nbsp;MB.
                  </Text>
                )}
                {uploadAlert && (
                  <Text c="red" size="xs" fw={500}>
                    Library full — remove a model to upload another.
                  </Text>
                )}
              </Stack>
            </Box>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="images">
          <ImagesAPIMapped />
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}

